/**
 * カード — 家宝（かほう）10枚
 *
 * docs/01-ゲームデザイン.md §9 の「家宝 10枚 — 名刀、茶器、南蛮渡来品（天下点）」に対応する。
 *
 * 【設計方針】
 * 家宝は所持しているあいだ天下点（vp）になる札で、10枚すべてが実在の名物である。
 * 架空の宝物は1枚も置かない。所蔵先・指定・伝来がたどれるものだけを選んだ。
 *
 *   名刀（3）    三日月宗近・童子切安綱・圧切長谷部
 *   茶器（4）    初花肩衝・九十九髪茄子・古天明平蜘蛛・大井戸茶碗 銘 喜左衛門
 *   南蛮渡来（3） 南蛮胴具足・自鳴鐘・キリシタン版
 *
 * 合戦で使う札（timing: 'battle'）は docs/06-賭けと軍配勝負.md のホールデムに
 * 噛み合う形（formationMod / holeCardMod）だけで効かせる。
 * 「サイコロ+1」のような旧仕様の効果は書かない。
 * scope の読み方は core/effects.ts と gunji.ts に揃える：
 *   'attack' = 攻め手の役に作用 / 'defend' = 受け手の役に作用 / 'both' = 使用者の役に作用
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力（一向一揆・キリスト教・比叡山）は事実の記述に留める
 *   - 女性・民衆の存在を消さない。名物は刀工・鋳物師・陶工・研師といった
 *     職人の手仕事であり、所持者だけの物語ではない。ここは家宝札の担当範囲である
 *   - 通説と異説がある事項は caveat に必ず書く
 *     （圧切の逸話・久秀の爆死・ザビエルの献上品の「望遠鏡」など、
 *      名物には後世の物語がとくに多く付く。ここを黙って通さない）
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 *
 * 【解禁章】docs/01 §1 の章区分に従う。
 *   第一章 1467-1520 / 第二章 1521-1559 / 第三章 1560-1581 / 第四章 1582-1599
 * 南蛮渡来の3枚は渡来した年に合わせて availableFrom を置いてある。
 */

import type { Card } from '../types';

// ---------------------------------------------------------------------------
// 名刀
// ---------------------------------------------------------------------------

/** 天下五剣の一。所持者に女性（高台院）が確実な史料で確認できる数少ない名物。 */
export const MIKAZUKI_MUNECHIKA: Card = {
  id: 'card:kahou:mikazuki',
  name: '三日月宗近',
  reading: 'みかづきむねちか',
  type: 'kahou',
  cost: { zeni: 5 },
  timing: 'anytime',
  effects: [
    // 天下に知られた名物を持つこと自体が、家の格を示す
    { t: 'gain', res: 'ishin', n: 3 },
  ],
  vp: 3,
  note: {
    summary:
      '平安時代の刀工・三条宗近の作と伝える太刀。刃文に三日月形の模様が連なることが名の由来。豊臣秀吉の正室高台院が所持し、その遺品として徳川秀忠に贈られた。国宝。',
    detail: {
      1: '平安時代につくられたと伝えられる刀です。刃のもようが三日月の形に見えることから、この名がつきました。豊臣秀吉の妻だった高台院（ねね）が持っていて、そのあと徳川家につたわりました。いまは東京国立博物館にあります。',
      2: '山城の刀工・三条宗近の作と伝えられる太刀で、天下五剣と呼ばれる名刀のひとつ。刃文のなかに三日月の形をした打除（うちのけ）が連なって見えることから、この名がついた。伝来としては室町幕府の足利将軍家が持っていたとされ、のちに豊臣秀吉の正室である高台院（北政所）が所持し、その遺品として徳川秀忠に贈られて徳川将軍家に伝わった。1951年に国宝に指定され、1992年に東京国立博物館へ寄贈されて現在に至る。',
      3: '太刀、銘「三条」。山城国の刀工・三条宗近の作と伝えられ、直刀から反りをもつ日本刀へ形が移り変わる時期の作例として重視される。制作年代には諸説があり、古伝書の伝える一条天皇の治世（10世紀後半〜11世紀前半）とする見方と、12世紀ごろとする見方がある。刃文の内側に三日月形の打除が連なる働きが名の由来で、天下五剣（三日月宗近・童子切安綱・大典太光世・数珠丸恒次・鬼丸国綱）のひとつに数えられる。伝来は足利将軍家から豊臣秀吉の正室高台院（北政所、ねね）へ渡り、高台院の遺品として徳川秀忠に贈られたと伝えられる。名物の伝来は男性の武将の間だけを移動したように語られがちだが、この一振りは女性の所持を経て徳川家へ入っている。大名家の女性が化粧料として所領を持ち、道具を管理し、遺品として配分したことは各家の記録に見える。刀は刀工ひとりの作ではなく、地金を鍛える弟子、刃をつける研師、鞘師・柄巻師・金工といった職人の分業で仕上がるもので、名物の背後にはそうした職人の集団がいる。1933年に旧国宝、1951年に国宝に指定され、1992年に東京国立博物館へ寄贈された。',
    },
    year: [986, 1616],
    keywords: ['三日月宗近', '天下五剣', '三条宗近', '高台院', '国宝', '太刀'],
    caveat:
      '制作年代には10世紀後半〜11世紀前半とする説と12世紀ごろとする説があり、確定していません。「天下五剣」という呼び方がいつ誰によって定まったのかもはっきりしません。足利将軍家に伝わったとされる時期の記録は後世のものが多く、高台院より前の伝来については確実とは言えません。',
    gameNote:
      '盤上では「威信+3・天下点3」という最も格の高い家宝にしていますが、これは天下五剣という後世の評価をそのまま札の強さに置き換えたもので、当時この刀が実際に最上位と扱われていたことを示す同時代の格付けがあるわけではありません。',
  },
};

/** 天下五剣の一。名の由来は説話であり史実ではない、と本文で明示する。 */
export const DOUJIGIRI_YASUTSUNA: Card = {
  id: 'card:kahou:doujigiri',
  name: '童子切安綱',
  reading: 'どうじぎりやすつな',
  type: 'kahou',
  cost: { zeni: 4 },
  timing: 'anytime',
  effects: [
    // 家に代々伝わる重宝として、家中をまとめる働きをする
    { t: 'gain', res: 'ishin', n: 2 },
    { t: 'gain', res: 'gunryaku', n: 1 },
  ],
  vp: 2,
  note: {
    summary:
      '平安時代の伯耆国の刀工・安綱の作と伝える太刀。源頼光が酒呑童子を斬ったという説話が名の由来で、この説話自体は物語であって記録ではない。国宝。',
    detail: {
      1: '平安時代につくられたと伝えられる刀です。源頼光が大江山の鬼「酒呑童子」を切ったという物語から、この名がつきました。鬼の話はつくり話ですが、刀そのものは本物で、いまも残っています。足利将軍家から織田信長・豊臣秀吉・徳川家康へとつたわりました。',
      2: '伯耆国（現在の鳥取県中西部）の刀工・安綱の作と伝えられる太刀で、天下五剣のひとつ。名は、源頼光が大江山の酒呑童子を斬ったという説話にちなむ。この説話は『御伽草子』などに語られる物語であり、実際の出来事の記録ではない。伝来は足利将軍家から織田信長、豊臣秀吉、徳川家康へ渡り、徳川秀忠を経て越前松平家、さらに津山松平家に家宝として伝えられたとされる。1951年に国宝に指定され、現在は東京国立博物館が所蔵している。',
      3: '太刀、銘「安綱」。平安時代（10〜12世紀）の伯耆国の刀工・安綱の作と伝えられ、古伯耆物を代表する一振りとされる。天下五剣のひとつ。名称の由来である酒呑童子退治は、室町期に成立した『御伽草子』などに見える説話で、源頼光と四天王が大江山の鬼を討つという筋を持つが、これは物語であり、同時代の記録ではない。鬼として語られた存在を、山に住む集団や中央の支配に従わない人々の姿が投影されたものと読む研究もあるが、確定した解釈はない。伝来は室町幕府の足利将軍家から織田信長・豊臣秀吉・徳川家康へと移り、徳川秀忠から越前松平家に渡って、のち津山松平家の家宝として伝えられたとされる。津山藩では家宝として厳重に扱われ、藩の記録に管理の様子が残る。1951年に国宝に指定され、現在は東京国立博物館が所蔵する。名物の刀が代々の当主に受け継がれることは、その家の連続性を示す装置でもあり、刀そのものの切れ味とは別の意味を持っていた。',
    },
    year: [986, 1616],
    keywords: ['童子切安綱', '天下五剣', '伯耆安綱', '酒呑童子', '津山松平家', '国宝'],
    caveat:
      '酒呑童子を斬ったという話は『御伽草子』などに語られる説話であり、史実の記録ではありません。この刀が実際にその出来事に用いられたということもありません。足利将軍家より前の伝来、および織田信長・豊臣秀吉の所持についても、後世に編まれた刀剣の記録に依るところが大きく、同時代史料での裏付けは限られます。',
    gameNote:
      '盤上では「威信+2・軍略+1」としています。軍略が増えるのは、家に代々伝わる重宝が家中をまとめる働きをしたことを表した処理で、刀そのものに戦の采配を助ける力があるという意味ではありません。',
  },
};

/** 「圧し切り」の逸話は後世の記録である点を caveat に必ず書く。 */
export const HESHIKIRI_HASEBE: Card = {
  id: 'card:kahou:heshikiri',
  name: '圧切長谷部',
  reading: 'へしきりはせべ',
  type: 'kahou',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [
    // 名物を家臣に与えて働きに報いる。恩賞としての家宝
    { t: 'gain', res: 'hei', n: 2 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  vp: 2,
  note: {
    summary:
      '南北朝期の刀工・長谷部国重の作とされる刀。織田信長から黒田孝高（官兵衛）に与えられ、黒田家に伝わった。名は、刀を振り下ろさずに押し当てて切ったという逸話による。国宝。',
    detail: {
      1: '織田信長が持っていて、家来の黒田官兵衛にあたえた刀です。刀を振り下ろさずに、上からおしあてて切ったという話から「圧切（へしきり）」の名がつきました。この話は後の時代に書かれたものです。いまは福岡市博物館にあります。',
      2: '山城の刀工・長谷部国重の作とされる刀で、もとは長い太刀を磨り上げて（短く詰めて）現在の姿になっている。織田信長が所持し、のちに黒田孝高（官兵衛、如水）に与えられて黒田家に伝わった。名は、信長が棚の下に隠れた者を、刀を振り下ろさずに押し当てるようにして切ったという逸話にちなむ。刀身には本阿弥家による金象嵌の銘と「黒田筑前守」の名が入る。1953年に国宝に指定され、現在は福岡市博物館が所蔵している。',
      3: '刀、金象嵌銘「長谷部国重本阿（花押）／黒田筑前守」。南北朝時代に山城で活動した刀工・長谷部国重の作とされ、大磨上げ（もとの茎を詰めて短くすること）によって太刀から刀の姿になっている。長谷部派は正宗十哲のひとつに数えられるが、正宗十哲という枠組み自体が後世の整理であり、師弟関係の実態は明らかでない。名の由来とされる「圧し切り」の逸話は、棚の下に隠れた茶坊主を織田信長が刀を押し当てて切ったというもので、江戸時代の記録に見える。信長から黒田孝高に与えられた時期と理由については、中国方面の調略にかかわる功に対するものとする説などがあるが、確実な同時代史料は乏しい。刀身に切られた金象嵌銘は、江戸時代に刀剣の鑑定を家業とした本阿弥家によるもので、無銘の刀に作者と評価額を記す本阿弥家の折紙は、名物の価値を社会的に成立させる仕組みでもあった。刀は刀工一人の作ではなく、鍛冶とその弟子、研師、鞘師、柄巻師、金工など多くの職人の分業で仕上がる。名物の背後には、名を残さなかった職人の技術の蓄積がある。1953年に国宝に指定され、黒田家から福岡市に渡って、現在は福岡市博物館が所蔵する。',
    },
    year: [1336, 1600],
    keywords: ['圧切長谷部', '長谷部国重', '黒田孝高', '織田信長', '本阿弥家', '国宝'],
    caveat:
      '「棚ごと圧し切った」という逸話は江戸時代の記録に見えるもので、同時代の史料で確かめることはできません。織田信長が黒田孝高に与えた時期・理由についても諸説あり、確定していません。「正宗十哲」という括りも後世の整理で、そのまま師弟関係を示すものではありません。',
    gameNote:
      '盤上では「兵+2・威信+1」としています。名物を家臣に与えて働きに報いる行為を、動員できる兵の増加という形に置き換えたもので、刀を与えれば兵が増えるという意味ではありません。',
  },
};

// ---------------------------------------------------------------------------
// 茶器
// ---------------------------------------------------------------------------

/** 天下三肩衝の一。名物が恩賞として機能したことを盤上に出す。 */
export const HATSUHANA_KATATSUKI: Card = {
  id: 'card:kahou:hatsuhana',
  name: '初花肩衝',
  reading: 'はつはなかたつき',
  type: 'kahou',
  cost: { zeni: 5 },
  timing: 'anytime',
  effects: [
    // 名物を持つ者のもとに茶人と商人が集まる
    { t: 'gain', res: 'ishin', n: 2 },
    { t: 'gain', res: 'zeni', n: 2 },
  ],
  vp: 3,
  note: {
    summary:
      '中国から渡来した唐物の茶入。楢柴肩衝・新田肩衝とあわせて天下三肩衝と呼ばれた。足利義政が所持したと伝えられ、織田信長・豊臣秀吉・徳川家康の手を経て徳川将軍家に伝わった。重要文化財。',
    detail: {
      1: 'お茶の葉を入れる小さなつぼ（茶入）です。中国でつくられ、日本にわたってきました。足利義政が持っていたと伝えられ、そのあと織田信長・豊臣秀吉・徳川家康へとつたわりました。名物の茶入は、一つの国とひきかえになるほど大切にされたといわれます。',
      2: '中国の南宋から元の時代につくられたと推定される陶製の茶入で、肩が張った形から「肩衝」と呼ばれる。楢柴肩衝・新田肩衝とあわせて天下三肩衝と称された大名物。足利義政が所持したと伝えられ、戦国期には織田信長、豊臣秀吉、徳川家康と持ち主が移り、徳川将軍家に伝来した。織田信長は名物の茶道具を集め（名物狩り）、茶会を開く許可や名物の下賜を家臣への褒賞として用いたとされる。現在は徳川記念財団が所蔵する重要文化財。',
      3: '唐物肩衝茶入。中国の南宋〜元代の作と推定され、日本には室町期に渡来したとみられる。楢柴肩衝（筑前博多の島井宗室が所持、のち豊臣秀吉へ）、新田肩衝（大友宗麟から秀吉へ、大坂夏の陣で被災し漆で修復されて水戸徳川家へ）とともに天下三肩衝と呼ばれた大名物である。伝来は足利義政の所持に始まると伝えられ、鳥居引拙・大文字屋など京・堺の町衆の手を経て、織田信長、豊臣秀吉、徳川家康へと移り、徳川将軍家に伝わった。信長が名物の茶道具を強く求め、茶会を開く許可を家臣への褒賞としたことは『信長公記』などから知られ、これを「御茶湯御政道」と呼ぶ説明が広く行われる。ただしこの語や、茶器一つが一国に相当したという言い方には後世の理解が入っており、近年は信長の茶の湯政策を整った制度として語ることに慎重な見方も出されている。注意すべきは、名物の値を決めていたのが大名ではなく、堺・博多・京の商人と茶人だったことである。武将は彼らの評価に従って買い、贈り、奪った。茶の湯は町衆の文化としても広がり、茶会には商家の主人や僧が加わった。現在は徳川記念財団の所蔵で、重要文化財に指定されている。',
    },
    year: [1450, 1616],
    keywords: ['初花肩衝', '天下三肩衝', '唐物茶入', '名物狩り', '足利義政', '徳川将軍家'],
    caveat:
      '「茶器一つが一国に値した」という言い方や「御茶湯御政道」という語は、後世の理解を含んだ説明です。近年は、織田信長の茶の湯政策を整った制度としてまとめて語ることに慎重な見方も出されています。伝来の細部についても複数の記録があり、一致していません。',
    gameNote:
      '盤上では「威信+2・銭+2・天下点3」としています。銭が増えるのは、名物を持つ者のもとに茶人や商人が集まったことを表した処理で、茶入そのものが収入を生んだわけではありません。',
  },
};

/** 二度焼けて二度修復された茶入。物が伝わる過程そのものが学びになる。 */
export const TSUKUMO_NASU: Card = {
  id: 'card:kahou:tsukumonasu',
  name: '九十九髪茄子',
  reading: 'つくもがみなす',
  type: 'kahou',
  cost: { zeni: 4 },
  timing: 'council',
  effects: [
    // 名物を献じて和を結ぶ。戦わずに事を収める道具として使われた
    { t: 'gain', res: 'gunryaku', n: 2 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  vp: 2,
  note: {
    summary:
      '茄子の形をした唐物の茶入。1568年に松永久秀が織田信長に献じたことが『信長公記』に見える。二度の戦火で焼けたが、そのつど漆で修復されて現存する。',
    detail: {
      1: 'なすの形をした、お茶の葉を入れる小さなつぼです。松永久秀が織田信長にさしあげたと記録に残っています。そのあと二度も火事にあってこわれましたが、うるしの職人がつなぎ合わせてなおしました。いまも東京の美術館で見ることができます。',
      2: '茄子の形をした唐物茶入で、大名物のひとつ。足利義満が所持したと伝えられ、名は『伊勢物語』の「つくも髪」の歌にちなむとも、村田珠光が九十九貫文で買い求めたことによるともいう。1568年（永禄11年）、松永久秀が上洛した織田信長に献じたことが『信長公記』に記されている。その後、本能寺の変で焼けたと伝えられ、豊臣秀吉のもとに移り、1615年の大坂夏の陣で再び火を被った。焼け跡から破片が拾い集められ、塗師の藤重藤元・藤厳父子が漆で継いで修復したと伝えられる。現在は静嘉堂文庫美術館が所蔵する。',
      3: '唐物茄子茶入、大名物。名称の由来には、『伊勢物語』第六十三段の「百年に一年たらぬつくも髪」の歌に掛けたとする説と、村田珠光が九十九貫文で購入したことによるとする説があり、どちらとも決めがたい。伝来は足利義満・義政の所持と伝えられ、山名家、朝倉家、松永久秀と移った。永禄11年（1568年）、足利義昭を奉じて入京した織田信長のもとへ松永久秀が伺候し、この茶入を献じたことが『信長公記』に見える。名物の献上が臣従の意思表示として機能した具体例であり、戦わずに関係を取り結ぶ道具として茶器が使われたことを示す。天正10年（1582年）の本能寺の変で焼損したと伝えられ、その後豊臣家に伝わって、慶長20年（1615年）の大坂夏の陣で再び焼けた。焼け跡の灰の中から破片を探し出し、漆で継いで形を戻したのが塗師の藤重藤元・藤厳父子であるとされる。修復後は藤重家に下され、のち岩崎家の所蔵となって、現在は静嘉堂文庫美術館にある。名物が残るのは所持者の権勢によるのではなく、破片を拾い、漆で継いだ職人の手による——この茶入はそれをそのまま示している。焼損と修復の経緯には伝承的な記述も混じっており、どこまでが当時の記録に基づくかは慎重に見る必要がある。',
    },
    year: [1568, 1615],
    keywords: ['九十九髪茄子', '唐物茶入', '松永久秀', '織田信長', '藤重藤元', '静嘉堂文庫'],
    caveat:
      '名の由来には『伊勢物語』の歌に掛けたとする説と、村田珠光が九十九貫文で買ったとする説の両方があり、決着していません。本能寺の変や大坂夏の陣での焼損と、そこからの修復の経緯にも伝承的な記述が混じっており、細部をそのまま事実として扱うことはできません。',
    gameNote:
      '盤上では「軍略+2・威信+1」としています。名物を献じて事を収めるという用いられ方を数値にしたもので、実際には献上すれば必ず和が結ばれたわけではありません。松永久秀はのちに織田信長と再び対立しています。',
  },
};

/** 「爆死」伝説を正す札。docs/05 §4-2 の代表例として書く。 */
export const KOTENMYOU_HIRAGUMO: Card = {
  id: 'card:kahou:hiragumo',
  name: '古天明平蜘蛛',
  reading: 'こてんみょうひらぐも',
  type: 'kahou',
  cost: { zeni: 3 },
  timing: 'battle',
  effects: [
    // 渡さぬと決めて城に籠もる。受け手の役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'defend' },
  ],
  vp: 2,
  note: {
    summary:
      '松永久秀が所持した茶の湯釜。蜘蛛が這うような平たい形が名の由来。1577年の信貴山城の戦いののち失われたとされるが、「釜を抱いて爆死した」という話は後世の軍記物によるもの。',
    detail: {
      1: 'お茶の湯をわかす鉄の釜です。くもが地面をはうような平たい形なので「平蜘蛛」とよばれました。松永久秀が持っていて、織田信長がほしがってもわたさなかったと伝えられます。「釜といっしょに爆発して死んだ」という話は、後の時代につくられた物語です。',
      2: '下野の天明（現在の栃木県佐野市周辺）で作られた古天明の茶の湯釜と伝えられ、蜘蛛が這うように平たい形をしていることから平蜘蛛と呼ばれた。大和の松永久秀が所持し、織田信長が何度も所望したが応じなかったと伝えられる。1577年（天正5年）の信貴山城の戦いで久秀が自害したのち、釜も失われたとされる。よく知られる「釜に火薬を詰め、抱えて爆死した」という話は江戸時代の軍記物に見えるもので、当時に近い『信長公記』は自害とのみ記している。',
      3: '茶の湯釜。下野天明（佐野天明）の釜と伝えられる。中世の茶の湯釜は筑前芦屋と下野天明が二大産地で、鋳物師（いもじ）と呼ばれる職人の集団が鋳造した。芦屋釜が滑らかな肌と絵文様を特色とするのに対し、天明釜は荒々しい鉄肌を持ち、わび茶の広がりのなかで評価された。平蜘蛛の名は、蜘蛛が這うように平たく低い形姿による。大和の松永久秀が所持し、織田信長が繰り返し所望したが応じなかったと伝えられ、天正5年（1577年）の信貴山城の戦いののち所在が知れなくなった。「釜に火薬を詰めて抱き、爆死した」という劇的な最期は江戸時代の軍記物に見える記述で、同時代に近い『信長公記』は久秀が自害したと記すのみである。近年の研究では、松永久秀を「主君殺し・将軍殺し・大仏焼き討ち」の三悪をなした人物として語る評価そのものが後世に形づくられたものであり、東大寺大仏殿の焼失も三好方との戦闘のなかでの出火であって久秀の意図によるとは言いきれない、といった見直しが進んでいる。名物にまつわる劇的な物語は、その物語が誰によっていつ書かれたかを確かめてから読む必要がある。',
    },
    year: [1560, 1577],
    keywords: ['古天明平蜘蛛', '茶の湯釜', '松永久秀', '信貴山城', '天明釜', '鋳物師'],
    caveat:
      '「平蜘蛛の釜に火薬を詰めて抱え、爆死した」という話は江戸時代の軍記物によるもので、同時代に近い『信長公記』には松永久秀が自害したとあるだけです。久秀を「三悪の人物」とする評価も後世に形づくられたもので、近年の研究では見直されています。',
    gameNote:
      '盤上では「受け手の役が1段階上がる」としています。渡さぬと決めて城に籠もったという伝承をルールに翻訳したもので、茶釜そのものに防御の力があるという意味ではありません。また、この札は籠城という行為を推奨するものでも、その結末を美化するものでもありません。',
  },
};

/** 名もない職人が日常の器として焼いたものが国宝になった、という一枚。 */
export const KIZAEMON_IDO: Card = {
  id: 'card:kahou:kizaemon',
  name: '大井戸茶碗 銘 喜左衛門',
  reading: 'おおいどちゃわん めい きざえもん',
  type: 'kahou',
  cost: { zeni: 1 },
  timing: 'council',
  effects: [
    // 高価な唐物ではないものに価値を見いだす。安く手に入るが格は高い
    { t: 'gain', res: 'ishin', n: 2 },
  ],
  vp: 2,
  note: {
    summary:
      '朝鮮半島で焼かれた高麗茶碗。もとは日常づかいの器と考えられている。わび茶のなかで最上級の名物とされ、高麗茶碗のうち唯一、国宝に指定されている。',
    detail: {
      1: '朝鮮（いまの韓国・北朝鮮のあたり）でつくられた茶わんです。もともとは特別なものではなく、ふだん使いの器だったと考えられています。それを日本の茶の湯の人たちが「よい」と見て、たいへん大切にしました。いまは国宝で、京都のお寺にあります。',
      2: '16世紀ごろに朝鮮半島で焼かれた高麗茶碗のうち、井戸茶碗と呼ばれる一群を代表する茶碗。名は所持者だった竹田喜左衛門にちなむ。高台のまわりに生じた釉の縮れは梅花皮（かいらぎ）と呼ばれ、井戸茶碗の見どころとされる。中国から渡来した唐物を上位に置く価値観に対し、千利休らのわび茶の広がりのなかで、名も知られない陶工が日常の器として焼いたものに高い価値が置かれるようになった。江戸時代に大名家を渡り歩き、松江藩主の松平不昧も所持した。現在は京都・大徳寺の塔頭である孤篷庵が所蔵する国宝である。',
      3: '大井戸茶碗、銘「喜左衛門」。李氏朝鮮前期、16世紀ごろの朝鮮半島南部で焼かれたとみられる高麗茶碗で、高麗茶碗としては唯一の国宝であり、国宝に指定された茶碗8碗のうち唯一の朝鮮製である。名は所持者の竹田喜左衛門に由来し、以後、本多家・中村家を経て松江藩主松平治郷（不昧）が所持し、現在は京都・大徳寺塔頭の孤篷庵にある。井戸茶碗の見どころとされるのは、轆轤目の残るおおらかな形、枇杷色の釉、そして高台まわりに釉が縮れて粒立った梅花皮である。これらはいずれも、丁寧に仕上げようとして生じたものではなく、量産のなかで自然に生じた景色である。井戸茶碗が本来どのような用途の器だったかについては、日常の飯茶碗とする説、祭祀用の器とする説などがあり、定説はない。確かなのは、これを焼いたのが名を記録されない朝鮮の陶工であり、茶碗としての価値は日本の茶人が後から与えたものだという点である。わび茶を「質素で安価なものを尊ぶ思想」と説明することがあるが、名物の井戸茶碗は当時から極めて高価に取引されており、単純な質素志向ではない。なお、文禄・慶長の役の際に朝鮮半島から多くの陶工が日本へ連れて来られ、その技術が唐津・萩・薩摩・有田などの窯につながった。茶碗の歴史は、こうした強制的な連行の歴史とも切り離せない。',
    },
    year: [1500, 1600],
    keywords: ['井戸茶碗', '高麗茶碗', '梅花皮', 'わび茶', '孤篷庵', '国宝'],
    caveat:
      '国宝に指定された茶碗は、唐物天目5碗（曜変天目3碗・油滴天目1碗・玳玻天目1碗）と国焼2碗（志野茶碗 銘卯花墻・本阿弥光悦 白楽茶碗 銘不二山）を含め計8碗あり、この井戸茶碗が唯一の国宝茶碗というわけではありません。井戸茶碗がもともと何に使われた器だったのかは、日常の飯茶碗とする説や祭器とする説があり、定まっていません。「所持した者が次々に腫物を患った」という話も後世の言い伝えです。わび茶を「安価で質素なものを尊ぶ思想」と説明するのも単純化で、名物の井戸茶碗は当時から非常に高値で取引されました。',
    gameNote:
      '盤上では「銭1で買えて威信+2・天下点2」という、値段のわりに格の高い札にしています。高価な唐物でないものに価値が見いだされたことを遊びの上で表したもので、この茶碗が実際に安価に入手できたという意味ではありません。',
  },
};

// ---------------------------------------------------------------------------
// 南蛮渡来
// ---------------------------------------------------------------------------

/** 渡来した鉄の胴を日本の甲冑師が仕立て直したもの。鉄砲以後なので availableFrom 3。 */
export const NANBANDOU_GUSOKU: Card = {
  id: 'card:kahou:nanbandou',
  name: '南蛮胴具足',
  reading: 'なんばんどうぐそく',
  type: 'kahou',
  cost: { zeni: 3 },
  timing: 'battle',
  effects: [
    // 弾をそらす形の鉄の胴。着用者の役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'both' },
  ],
  vp: 1,
  availableFrom: 3,
  note: {
    summary:
      'ヨーロッパから渡来した鉄製の胴を、日本の甲冑師が草摺や面頬と組み合わせて具足に仕立て直したもの。徳川家康が用いたと伝わるものが久能山東照宮と紀州東照宮に残る。',
    detail: {
      1: 'ヨーロッパから船で運ばれてきた鉄のよろいの胴を、日本の職人が作りなおしたよろいです。まん中に山の形の筋があって、鉄砲のたまをすべらせるようにできています。徳川家康が使ったと伝えられるものが、静岡と和歌山に残っています。',
      2: '16世紀後半以降、ポルトガル船などによってもたらされたヨーロッパ製の胴（キュイラス）を、日本の甲冑師が草摺・袖・面頬などと組み合わせて仕立て直した具足。胴の前面中央に鎬（しのぎ）と呼ばれる稜線を立てることで、当たった弾や槍先をそらす構造になっている。鉄砲が広く使われるようになった時代の武具として、日本の当世具足の作り方にも影響を与えた。徳川家康が用いたと伝わるものが久能山東照宮（静岡）と紀州東照宮（和歌山）に伝来し、いずれも重要文化財に指定されている。',
      3: '南蛮胴具足とは、ヨーロッパで作られた鉄製の胴甲を輸入し、日本式の草摺・袖・面頬・兜と組み合わせて具足として仕立てたものをいう。ヨーロッパの胴は一枚ないし前後二枚の広い鉄板を打ち出して作られ、前面中央に鎬を立てて弾や刺突をそらす形をとる。日本の当世具足が小札（こざね）を綴じ合わせる構成から板物へと移っていく流れとも重なり、日欧の技術が接触した具体例として重視される。徳川家康所用と伝えるものが複数あり、紀州東照宮に伝わる一領は兜・胴ともにヨーロッパ製という点で類例が少なく、前胴に十か所の火縄銃の弾痕が残ることから、強度を試した「試し胴」であったと考えられている。久能山東照宮には関ヶ原の戦いに用いたと伝える具足が納められている。いずれも徳川頼宣ら子孫が家康の遺品として東照宮に奉納したもので、重要文化財に指定されている。仕立て直しを担ったのは日本の甲冑師で、輸入品をそのまま着るのではなく、面頬や草摺を付けて日本の戦い方に合う形へ作り替えている。渡来品をそのまま受け取るのではなく作り変えるという態度は、鉄砲の国産化とも共通する。ただし、どの具足を家康がいつ着用したかは伝承による部分が大きく、弾痕が家康自身の試射によるものと確定しているわけでもない。',
    },
    year: [1570, 1616],
    keywords: ['南蛮胴具足', '当世具足', '甲冑師', '久能山東照宮', '紀州東照宮', '南蛮渡来'],
    caveat:
      '徳川家康所用と伝わる南蛮胴具足は複数あり、どれをいつ着用したかは伝承による部分が大きく、確実ではありません。紀州東照宮の具足に残る弾痕についても、家康自身が試射させたものと確定しているわけではありません。',
    gameNote:
      '盤上では「使用者の役が1段階上がる」としていますが、実際には南蛮胴を着ていれば戦に勝てたわけではなく、着用できたのはごく限られた身分の者だけです。第III章から解禁しているのは、鉄砲伝来以後に渡来した武具であることに合わせた処理です。',
  },
};

/** ザビエルの献上品。「望遠鏡」の記述が時代と合わないことを caveat に書く。 */
export const JIMEISHOU: Card = {
  id: 'card:kahou:jimeishou',
  name: '自鳴鐘',
  reading: 'じめいしょう',
  type: 'kahou',
  cost: { zeni: 4 },
  timing: 'council',
  effects: [
    // 珍しい渡来品が人と知らせを呼び寄せる
    { t: 'gain', res: 'zeni', n: 2 },
    { t: 'draw', n: 1 },
  ],
  vp: 2,
  availableFrom: 2,
  note: {
    summary:
      'ぜんまい仕掛けの西洋の時計。1551年にフランシスコ・ザビエルが周防の大内義隆に贈った品のひとつと伝えられる。現存最古のものは徳川家康に贈られ久能山東照宮に伝わる洋時計。',
    detail: {
      1: 'ぜんまいで動く西洋の時計です。1551年、フランシスコ・ザビエルが山口の大名・大内義隆に会ったとき、めずらしい品といっしょにおくったと伝えられます。いま日本に残っているいちばん古い西洋の時計は、徳川家康におくられたもので、静岡の久能山東照宮にあります。',
      2: 'ぜんまい仕掛けで動く西洋の機械時計。1551年（天文20年）、フランシスコ・ザビエルは周防山口で大内義隆に会い、インド総督とゴア司教の書状とともに置時計・眼鏡・鏡・ガラスの水差し・楽器・書物・絵などの品を贈ったと伝えられる。義隆はこれに応じて領内での布教を認めた。現存する日本最古のぜんまい式時計は、1611年にスペイン国王から徳川家康に贈られたもので、文字盤の下に「1581年マドリードでハンス・デ・エバロが私を作った」という銘がある。久能山東照宮に伝わり、重要文化財に指定されている。',
      3: '自鳴鐘は、ぜんまいと歯車で動き、時刻に鐘を鳴らす西洋式の機械時計を指す語である。天文20年（1551年）、フランシスコ・ザビエルは周防山口で大内義隆に面会し、インド総督とゴア司教の書状に添えて十数点の品を献じたと伝えられる。その中に置時計、眼鏡、鏡、ガラス製の水差し、鍵盤楽器、書物、絵画などがあったとされ、義隆はこれを受けて山口での布教を認め、廃寺となっていた大道寺を住居兼教会として与えた。ここで重要なのは、宣教師が贈り物と技術を携えて大名に近づき、大名の側は珍奇な品と海外との回路を得る利益から布教を認めた、という双方の関係である。現存する日本最古のぜんまい式時計は、久能山東照宮に伝わる洋時計で、文字盤下部に「1581年マドリードでハンス・デ・エバロが私を作った」という銘を持つ。慶長14年（1609年）に上総沖で難破したスペイン船の乗員を日本側が救助したことへの返礼として、慶長16年（1611年）にスペイン国王から徳川家康に贈られたものである。2014年の大英博物館による調査で、底面の銘板の下にさらに古い「1573年ブリュッセル、ニコラウス・ド・トロエステンベルク」の銘があることが判明した。日本では昼と夜をそれぞれ六等分する不定時法が用いられていたため、一定の速さで進む西洋の時計はそのままでは使いにくく、家康もこれを飾って眺めたにとどまったとされる。のちに日本の職人は不定時法に合わせて時刻の刻みが変わる和時計を作り出しており、渡来した技術をそのまま受け取るのではなく作り変えたという点で、鉄砲や南蛮胴と共通する。',
    },
    year: [1551, 1611],
    keywords: ['自鳴鐘', '洋時計', 'ザビエル', '大内義隆', '久能山東照宮', '和時計'],
    caveat:
      'ザビエルが大内義隆に贈った品の一覧は後世の記録によるもので、書物によって内容が違います。「望遠鏡」を挙げるものがありますが、望遠鏡が作られるのは1608年ごろとされ、1551年の贈り物に含まれていたとは考えにくいところです。渡来品の記録は、伝えられる品目そのものを慎重に見る必要があります。',
    gameNote:
      '盤上では「銭+2・カードを1枚引く」としています。カードを引くのは、珍しい渡来品のもとに人と知らせが集まったことを表した処理です。第II章から解禁しているのは、1551年の献上に合わせたものです。',
  },
};

/** 活字がもたらしたのは軍事ではなく言葉の記録。日葡辞書と天草版を軸に書く。 */
export const KIRISHITAN_BAN: Card = {
  id: 'card:kahou:kirishitanban',
  name: 'キリシタン版',
  reading: 'きりしたんばん',
  type: 'kahou',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [
    // 活字で刷られた書物が知識を広める
    { t: 'draw', n: 2 },
    { t: 'gain', res: 'gunryaku', n: 1 },
  ],
  vp: 2,
  availableFrom: 4,
  note: {
    summary:
      '天正遣欧少年使節が1590年に持ち帰ったヨーロッパ式の活字印刷機で刷られた書物。『天草版平家物語』『日葡辞書』などがあり、当時の話し言葉を今に伝えている。',
    detail: {
      1: '1590年、ヨーロッパへ送られた四人の少年たちが帰ってきたとき、活字で本を刷る機械を持ち帰りました。この機械で刷られた本を「キリシタン版」といいます。『平家物語』や、日本語とポルトガル語の辞書がつくられ、そのころの話しことばがどんなだったかがわかります。',
      2: '1582年、大友義鎮（宗麟）・大村純忠・有馬晴信の名代として、伊東マンショら四人の少年がローマへ派遣された（天正遣欧少年使節）。彼らは1590年に帰国し、ヨーロッパ式の活字印刷機を持ち帰った。この機械によって加津佐・天草・長崎などで日本語の書物が印刷され、キリシタン版と呼ばれる。ローマ字で書かれた『天草版平家物語』『天草版伊曽保物語』や、約3万2千語を収める日本語・ポルトガル語辞典『日葡辞書』などが刊行された。幕府の禁教政策によって1610年代には印刷が途絶えた。',
      3: '天正10年（1582年）、イエズス会の巡察師ヴァリニャーノの計画により、大友義鎮・大村純忠・有馬晴信の名代として伊東マンショ・千々石ミゲル・原マルチノ・中浦ジュリアンの四人がヨーロッパへ派遣された。一行は1590年に帰国し、ヨーロッパ式の活字印刷機とその技術を持ち帰った。印刷の実務には、使節に同行してリスボンなどで印刷術を学んだコンスタンチノ・ドラードらが携わったとされる。この機械で加津佐・天草・長崎などで刷られた書物を総称してキリシタン版と呼び、教理書のほか、日本語学習のための語学書や文学作品が含まれた。『天草版平家物語』『天草版伊曽保物語（イソップ物語）』はローマ字で当時の口語に近い文体を写しており、書き言葉の記録からは分からない16世紀末の日本語の発音・語彙・語法を知る第一級の史料になっている。慶長8年〜9年（1603-04年）に長崎で刊行された『日葡辞書』は約3万2千語を収め、語義に加えて地域差や身分・性別による言い方の違いにも触れており、当時の言葉の広がりを伝える。禁教の強化にともない、1610年代までに国内での印刷は途絶え、機械はマカオへ運ばれたとされる。なお、ほぼ同じ時期に朝鮮半島から伝わった活字技術による古活字版（慶長勅版や駿河版など）も行われており、「日本初の活版印刷」という言い方は、どちらの系統を指すかで変わる。ここではキリスト教の布教の是非には立ち入らないが、宣教にともなって入った技術が、結果として当時の日本語を記録に残したという事実は押さえておきたい。',
    },
    year: [1590, 1614],
    keywords: ['キリシタン版', '天正遣欧少年使節', '日葡辞書', '天草版平家物語', '活字', '古活字版'],
    caveat:
      '「キリシタン版が日本初の活版印刷である」という言い方には注意が要ります。ほぼ同じ時期に、朝鮮半島から伝わった活字技術による古活字版（慶長勅版・駿河版など）も行われており、どちらを「初」とするかは、どの技術系統を指すかによって変わります。',
    gameNote:
      '盤上では「カードを2枚引く・軍略+1」としています。書物によって知識が広まることを札の補充で表した処理で、キリシタン版が軍事や政治に直接役立ったという意味ではありません。第IV章から解禁しているのは、1590年の帰国に合わせたものです。',
  },
};

// ---------------------------------------------------------------------------
// まとめ
// ---------------------------------------------------------------------------

/** 家宝カード 10枚。すべて実在の名物で、所持しているあいだ天下点（vp）になる。 */
export const KAHOU_CARDS: readonly Card[] = [
  // 名刀
  MIKAZUKI_MUNECHIKA,
  DOUJIGIRI_YASUTSUNA,
  HESHIKIRI_HASEBE,
  // 茶器
  HATSUHANA_KATATSUKI,
  TSUKUMO_NASU,
  KOTENMYOU_HIRAGUMO,
  KIZAEMON_IDO,
  // 南蛮渡来
  NANBANDOU_GUSOKU,
  JIMEISHOU,
  KIRISHITAN_BAN,
];
